import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { CountryCodeHeader } from 'shared/ApiProxyPluginHeaders';

import { CookieKeys } from 'src/common/constants';
import { tryQuery } from 'src/common/graphql/graphqlHelper';
import isServer from 'src/common/isServer';
import ScrollTopButton from 'src/components/ScrollTopButton/ScrollTopButton';
import useScrollTopButton from 'src/components/ScrollTopButton/useScrollTopButton';
import { apolloContextWithApiV2AlcClientName } from 'src/modules/ApolloWrapper/constants';
import { getCountryWithSGFallback } from 'src/modules/ClientConfig/Selectors';
import { JobSearchFiltersResponse } from 'src/modules/Opportunities/pages/Explore/Components/FilterSection/types';
import {
  findHierarchicalLocationsBySlug,
  getJobSearchFiltersWithoutJobCategoriesQuery,
  getPopularSearchesQuery,
} from 'src/modules/Opportunities/pages/Explore/graphql';
import {
  createJobSearchFiltersQueryDataFromExploreRouterQuery,
  createSearchJobsQueryDataFromExploreRouterQuery,
} from 'src/modules/Opportunities/pages/Explore/graphqlUtils';
import {
  FindHierarchicalLocationBySlugResponse,
  SearchRouterQueryKey,
} from 'src/modules/Opportunities/pages/Explore/types';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';
import { getIsAuthenticated } from 'src/modules/Session/Selectors';
import Error404Page from 'src/pages/Error404Page';
import Error410Page from 'src/pages/Error410Page';

import {
  getFilterSlugValueFromQuery,
  getLocationFromResponse,
  getQueryValue,
  getSearchSuggestions,
} from '../../../helpers';
import { useGetHierarchicalLocationsBySlug } from '../../../hooks/useGetHierarchicalLocationBySlug';
import { useGetJobSearchFilters } from '../../../hooks/useGetJobSearchFilters';
import { useGetPopularSearchesData } from '../../../hooks/useGetPopularSearchesData';
import {
  tryPreloadGetSearchJobs,
  useGetSearchJobs,
} from '../../../hooks/useGetSearchJobs';
import { getSearchErrorStatusCode } from './getSearchErrorStatusCode';
import { InternalSearchExploreTab } from './InternalSearchExploreTab';

const JobTabNavigation = dynamic(() =>
  import('src/modules/Opportunities/components/JobTabNavigation').then(
    (mod) => mod.JobTabNavigation
  )
);

interface InternalSearchExplorePageProps {
  statusCode?: number;
}

const QueryExplorePageComponent = ({
  statusCode,
}: InternalSearchExplorePageProps) => {
  const [showScrollTopButton, handleScrollTopButtonClick] =
    useScrollTopButton(null);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const router = useRouter();
  const { query } = router;

  const countryCode = useSelector(getCountryWithSGFallback);
  const {
    [SearchRouterQueryKey.LocationId]: locationIdFromFilter = undefined,
  } = query;

  const { hierarchicalLocation: locationResponse } =
    useGetHierarchicalLocationsBySlug(query);

  const locationId = useMemo(() => {
    return (
      (locationResponse && locationResponse?.id) ||
      getQueryValue(locationIdFromFilter)
    );
  }, [locationIdFromFilter, locationResponse]);

  const jobSearchQuery = useMemo(() => {
    return createSearchJobsQueryDataFromExploreRouterQuery(
      query,
      countryCode,
      locationId
    );
  }, [countryCode, locationId, query]);

  const filterQuery = useMemo(() => {
    return createJobSearchFiltersQueryDataFromExploreRouterQuery(
      query,
      countryCode,
      locationResponse
    );
  }, [countryCode, locationResponse, query]);

  const { data: jobSearchResponse, error: getJobSearchResponseError } =
    useGetSearchJobs({ jobSearchQuery });
  if (getJobSearchResponseError) {
    console.error(
      'QueryExplorePageError searching for jobs',
      getJobSearchResponseError
    );
  }

  const { filterResponse, error: filterResponseError } =
    useGetJobSearchFilters(filterQuery);
  if (filterResponseError) {
    console.error(
      'QueryExplorePageError fetching filters',
      filterResponseError
    );
  }

  const [storedSearches, setStoredSearches] = useState('');
  useEffect(() => {
    setStoredSearches(Cookies.get(CookieKeys.pastJobSearchConditions) || '');
  }, []);

  const { popularSearches, error: getPopularSearchesError } =
    useGetPopularSearchesData();

  if (getPopularSearchesError) {
    console.error(
      'QueryExplorePageError fetching filters',
      getPopularSearchesError
    );
  }

  const searchSuggestions = getSearchSuggestions(
    storedSearches,
    popularSearches
  );

  if (statusCode === 410) {
    return <Error410Page />;
  }

  if (statusCode === 404) {
    return <Error404Page />;
  }

  return (
    <>
      <ScrollTopButton
        // @ts-ignore
        onClick={handleScrollTopButtonClick}
        // @ts-ignore
        isVisible={showScrollTopButton}
      />
      {isAuthenticated && <JobTabNavigation />}
      <InternalSearchExploreTab
        jobSearchQuery={jobSearchQuery}
        filterQuery={filterQuery}
        jobSearchResponse={jobSearchResponse}
        filterResponse={filterResponse}
        searchSuggestions={searchSuggestions}
      />
    </>
  );
};

const InternalSearchExplorePage: NextPage = QueryExplorePageComponent;

InternalSearchExplorePage.getInitialProps = async ({
  reduxStore,
  apolloClient,
  query,
  pathname,
  res,
}) => {
  if (!isServer || !res) {
    return {};
  }
  const state = reduxStore.getState();
  const countryCode = getCountryWithSGFallback(state);
  const page = parseInt(query.page as string) || 1;
  const { slug, isLocationSlug } = getFilterSlugValueFromQuery(query);

  let location: HierarchicalLocation | undefined = undefined;

  if (isLocationSlug) {
    const { response, error } =
      await tryQuery<FindHierarchicalLocationBySlugResponse>({
        apolloClient: apolloClient,
        query: findHierarchicalLocationsBySlug,
        variables: {
          slug,
        },
        fetchPolicy: 'cache-first',
      });
    if (error) {
      console.error(
        'QueryExplorePageError fetching hierarchical location by slug',
        JSON.stringify(error)
      );
    }

    location = getLocationFromResponse(response);
  }

  const locationId: string | undefined = location && location.id;
  const jobSearchQuery = createSearchJobsQueryDataFromExploreRouterQuery(
    query,
    countryCode,
    locationId
  );

  const { error: jobSearchError } = await tryPreloadGetSearchJobs({
    apolloClient,
    jobSearchQuery,
    page,
  });

  if (jobSearchError) {
    console.error(
      'QueryExplorePageError searching for jobs',
      JSON.stringify(jobSearchError)
    );
    const statusCode = getSearchErrorStatusCode(pathname);
    res.statusCode = statusCode;
    return { statusCode };
  }

  const filterQuery = createJobSearchFiltersQueryDataFromExploreRouterQuery(
    query,
    countryCode,
    location
  );

  const { error: filterError } = await tryQuery<{
    getJobSearchFiltersV2: JobSearchFiltersResponse;
  }>({
    apolloClient: apolloClient,
    query: getJobSearchFiltersWithoutJobCategoriesQuery,
    context: {
      ...apolloContextWithApiV2AlcClientName,
      headers: {
        [CountryCodeHeader]: countryCode,
      },
    },
    variables: {
      filterCondition: filterQuery,
    },
    fetchPolicy: 'cache-first',
  });

  if (filterError) {
    console.error(
      'QueryExplorePageError fetching filters',
      JSON.stringify(filterError)
    );
  }

  // Fetch popular searches
  try {
    await apolloClient.query({
      query: getPopularSearchesQuery,
      variables: {
        countryCode: countryCode,
      },
    });
  } catch (error) {
    console.error('QueryExplorePageError fetching popular searches:', error);
  }

  return {};
};

export default InternalSearchExplorePage;
